import React, { useState } from 'react';
import axios from 'axios';
import { API } from '../context/AuthContext';
import { CheckCircle, XCircle, ArrowCounterClockwise, ChatCircle, X } from '@phosphor-icons/react';
import { toast } from 'sonner';

/**
 * Phase 1.5 — Builder Approve / Reject controls used inside the
 * /shared/:short_id portal. Posts to:
 *   POST /api/share/{shortId}/snags/{snagId}/respond
 *
 * Three actions:
 *   - Approve  (no reason needed; builder agrees to fix)
 *   - Reject   (reason required: picker + optional free-text detail)
 *   - Clear    (walk back an approve/reject decision)
 *
 * The component is purely presentational + a single network call.
 * The parent owns the snag list and re-fetches via onResponded() so
 * the visible state always matches the server.
 */
export const REJECTION_REASONS = [
    { code: 'settlement', label: 'Normal settlement' },
    { code: 'owner_damage', label: 'Owner-induced damage' },
    { code: 'within_tolerance', label: 'Within tolerance per NHBC' },
    { code: 'wear_and_tear', label: 'Wear & tear' },
    { code: 'out_of_warranty', label: 'Out of warranty period' },
    { code: 'cosmetic_not_defect', label: 'Cosmetic — not a defect' },
    { code: 'scope_excluded', label: 'Out of contract scope' },
    { code: 'other', label: 'Other (please specify)' },
];

export function reasonLabel(code) {
    return REJECTION_REASONS.find(r => r.code === code)?.label || 'Other';
}

/** Visible badge + reason summary that lives ABOVE the row's action
 *  buttons. Renders nothing when there's no builder response yet. */
export function BuilderResponseStrip({ snag, testId = '' }) {
    const resp = (snag.builder_response || '').toLowerCase();
    const disputed = !!snag.disputed;
    if (!resp && !disputed) return null;

    if (disputed && resp !== 'approved') {
        return (
            <div
                data-testid={testId || `builder-strip-${snag.id}`}
                className="mt-2 px-2.5 py-1.5 bg-[#DBEAFE] border-l-2 border-[#2563EB] text-[#1E3A8A] text-xs"
            >
                <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider font-semibold">
                    <ChatCircle size={12} weight="fill" /> Homeowner disputed
                </div>
                {snag.dispute_reason && <div className="mt-1">{snag.dispute_reason}</div>}
                {resp === 'rejected' && (
                    <div className="mt-1 text-[#3730A3]">
                        Builder had rejected this — reason: <em>{reasonLabel(snag.rejection_reason_code)}</em>
                        {snag.rejection_reason_detail ? ` — ${snag.rejection_reason_detail}` : ''}
                    </div>
                )}
            </div>
        );
    }

    if (resp === 'approved') {
        return (
            <div
                data-testid={testId || `builder-strip-${snag.id}`}
                className="mt-2 px-2.5 py-1.5 bg-[#E8F5FB] border-l-2 border-[#1FA5CF] text-[#1787AB] text-xs"
            >
                <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider font-semibold">
                    <CheckCircle size={12} weight="fill" /> Builder approved
                </div>
                {snag.builder_name && <div className="mt-0.5 text-[#52525B]">— {snag.builder_name}</div>}
            </div>
        );
    }

    if (resp === 'rejected') {
        return (
            <div
                data-testid={testId || `builder-strip-${snag.id}`}
                className="mt-2 px-2.5 py-1.5 bg-[#FEF3C7] border-l-2 border-[#F59E0B] text-[#92400E] text-xs"
            >
                <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider font-semibold">
                    <XCircle size={12} weight="fill" /> Builder rejected
                </div>
                <div className="mt-1">
                    <strong>{reasonLabel(snag.rejection_reason_code)}</strong>
                    {snag.rejection_reason_detail ? ` — ${snag.rejection_reason_detail}` : ''}
                </div>
                {snag.builder_name && <div className="mt-0.5 text-[#78350F]">— {snag.builder_name}</div>}
            </div>
        );
    }
    return null;
}

/** Approve / Reject / Clear buttons used by the builder portal. Each
 *  click hits the API directly; the parent re-fetches afterwards. */
export function BuilderResponseControls({ shortId, snag, builderName, onResponded }) {
    const [busy, setBusy] = useState(false);
    const [rejectOpen, setRejectOpen] = useState(false);
    const resp = (snag.builder_response || '').toLowerCase();

    const post = async (payload) => {
        setBusy(true);
        try {
            await axios.post(
                `${API}/share/${shortId}/snags/${snag.id}/respond`,
                payload,
                { headers: { 'Content-Type': 'application/json' } },
            );
            onResponded?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Could not save response. Please try again.');
        } finally {
            setBusy(false);
        }
    };

    const approve = () => post({ response: 'approved', builder_name: builderName });
    const clear = () => post({ response: 'cleared' });

    return (
        <>
            <div className="mt-2 flex flex-wrap items-center gap-1.5" data-testid={`builder-controls-${snag.id}`}>
                {resp !== 'approved' && (
                    <button
                        disabled={busy}
                        onClick={approve}
                        data-testid={`builder-approve-${snag.id}`}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-wider bg-[#1FA5CF] text-white hover:bg-[#1787AB] disabled:opacity-50 active:scale-95 transition-all duration-150"
                    >
                        <CheckCircle size={12} weight="fill" /> Approve
                    </button>
                )}
                {resp !== 'rejected' && (
                    <button
                        disabled={busy}
                        onClick={() => setRejectOpen(true)}
                        data-testid={`builder-reject-${snag.id}`}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-wider border border-[#F59E0B] text-[#B45309] hover:bg-[#FEF3C7] disabled:opacity-50 active:scale-95 transition-all duration-150"
                    >
                        <XCircle size={12} weight="bold" /> Reject
                    </button>
                )}
                {resp && (
                    <button
                        disabled={busy}
                        onClick={clear}
                        data-testid={`builder-clear-${snag.id}`}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-wider text-[#52525B] hover:bg-[#F4F4F5] disabled:opacity-50 active:scale-95 transition-all duration-150"
                    >
                        <ArrowCounterClockwise size={12} weight="bold" /> Undo
                    </button>
                )}
            </div>

            {rejectOpen && (
                <RejectModal
                    snag={snag}
                    onCancel={() => setRejectOpen(false)}
                    onSubmit={async (code, detail) => {
                        await post({
                            response: 'rejected',
                            reason_code: code,
                            reason_detail: detail,
                            builder_name: builderName,
                        });
                        setRejectOpen(false);
                    }}
                    busy={busy}
                />
            )}
        </>
    );
}

function RejectModal({ snag, onCancel, onSubmit, busy }) {
    const [code, setCode] = useState(snag.rejection_reason_code || 'settlement');
    const [detail, setDetail] = useState(snag.rejection_reason_detail || '');

    return (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-4 no-print" data-testid="builder-reject-modal">
            <div className="bg-white w-full max-w-md border border-[#18181B] max-h-[92vh] overflow-y-auto">
                <div className="flex items-center justify-between px-4 py-3 border-b border-[#18181B]">
                    <div className="flex items-center gap-2">
                        <XCircle size={18} weight="bold" color="#B45309" />
                        <span className="font-display font-semibold">Reject defect</span>
                    </div>
                    <button onClick={onCancel} className="p-1 hover:bg-[#F4F4F5]" aria-label="Close"><X size={18} /></button>
                </div>
                <div className="p-4">
                    <div className="text-sm text-[#52525B]">{snag.title}</div>
                    <div className="label-tiny mt-4">Reason</div>
                    <select
                        value={code}
                        onChange={(e) => setCode(e.target.value)}
                        data-testid="reject-reason-select"
                        className="w-full mt-1 border border-[#E4E4E7] focus:border-[#18181B] outline-none px-3 py-2 bg-white"
                    >
                        {REJECTION_REASONS.map(r => (
                            <option key={r.code} value={r.code}>{r.label}</option>
                        ))}
                    </select>
                    <div className="label-tiny mt-4 mb-1">
                        Details {code === 'other' && <span className="text-[#E53935] normal-case tracking-normal">(required)</span>}
                    </div>
                    <textarea
                        rows={3}
                        value={detail}
                        onChange={(e) => setDetail(e.target.value)}
                        placeholder="Optional context for the homeowner — why this isn't a defect under your contract / NHBC standards."
                        data-testid="reject-reason-detail"
                        className="w-full border border-[#E4E4E7] focus:border-[#18181B] outline-none px-3 py-2 text-sm"
                        maxLength={600}
                    />
                    <div className="text-[10px] text-[#A1A1AA] font-mono uppercase tracking-wider mt-1 text-right">
                        {detail.length}/600
                    </div>
                    <p className="mt-3 text-[11px] text-[#52525B]">
                        The homeowner will see this reason on their report and can choose to dispute it.
                    </p>
                    <div className="flex gap-2 mt-5">
                        <button
                            disabled={busy || (code === 'other' && !detail.trim())}
                            onClick={() => onSubmit(code, detail.trim())}
                            data-testid="reject-submit-btn"
                            className="flex-1 bg-[#B45309] text-white py-3 font-mono text-xs uppercase tracking-[0.2em] hover:bg-[#92400E] disabled:opacity-50 active:scale-95 transition-all duration-150"
                        >
                            {busy ? 'Saving…' : 'Send rejection'}
                        </button>
                        <button
                            onClick={onCancel}
                            className="px-4 py-3 font-mono text-xs uppercase tracking-[0.2em] border border-[#18181B] hover:bg-[#F4F4F5] active:scale-95 transition-all duration-150"
                        >Cancel</button>
                    </div>
                </div>
            </div>
        </div>
    );
}
